import { CalendarSlot, MeetingStatus, Patient, PatientMeetingData, ProposedTimeSlot } from "../types"
import { getMeetingStatus, rangesOverlap } from "../types-helpers"

const USER_COLORS = [
    "#1f77b4", // blue
    "#ff7f0e", // orange
    "#2ca02c", // green
    "#d62728", // red
    "#9467bd", // purple
    "#8c564b", // brown
    "#e377c2", // pink
    "#7f7f7f", // gray
    "#bcbd22", // olive
    "#17becf", // cyan
    "#4e79a7", // steel blue
    "#f28e2b", // amber
]

export const getUserColor = (colorIndex: number) => {
    if (colorIndex < 0) {
        return "lightGrey"
    }

    return USER_COLORS[colorIndex % USER_COLORS.length]
}

export const findConflicts = (
    proposedTimes: ProposedTimeSlot[],
    newTime: ProposedTimeSlot,
): {
    validTimes: ProposedTimeSlot[],
    conflicts: ProposedTimeSlot[],
} => {

    const validTimes: ProposedTimeSlot[] = []
    const conflicts: ProposedTimeSlot[] = []

    for (let time of proposedTimes) {
        if (rangesOverlap(time, newTime)) {
            conflicts.push(time)
        } else {
            validTimes.push(time)
        }
    }

    return {
        validTimes,
        conflicts,
    }
}

export const roundUpToNext30Min = (
    value: Date
): Date => {
    const updated = new Date(value)
    updated.setSeconds(0, 0)

    const minutes = updated.getMinutes()
    const remainder = minutes % 30

    if (remainder !== 0) {
        updated.setMinutes(minutes + (30 - remainder))
    }

    return updated
}

export const clampSlotStartToFuture = (
    slot: ProposedTimeSlot
): ProposedTimeSlot | null => {
    const minStart = roundUpToNext30Min(new Date())

    // no future portion left
    if (slot.endTime <= minStart) {
        return null
    }

    const startTime = slot.startTime < minStart ? minStart : slot.startTime

    if (startTime >= slot.endTime) {
        return null
    }

    const duration = slot.endTime.getTime() - slot.startTime.getTime()

    // Check if timeslot is at least 30min
    if (duration >= 1000 * 60 * 30) {
        return {
            ...slot,
            startTime,
        }
    }

    const newEndTime = new Date(slot.startTime)
    newEndTime.setMinutes(newEndTime.getMinutes() + 30)

    return {
        ...slot,
        startTime,
        endTime: newEndTime,
    }
}

export const openForExistingSlot = (
    newSlot: ProposedTimeSlot,
    finalSlots: ProposedTimeSlot[],
    sameMeetingSlots: ProposedTimeSlot[],
): {
    slot: ProposedTimeSlot | undefined
    validTimes: ProposedTimeSlot[]
    conflicts: ProposedTimeSlot[]
    sameMeetingConflicts: ProposedTimeSlot[]
    sameValid: ProposedTimeSlot[]
} => {
    const time = clampSlotStartToFuture(newSlot)

    if (!time) {
        return {
            slot: undefined,
            conflicts: [],
            sameMeetingConflicts: [],
            validTimes: [],
            sameValid: []
        }
    }

    // Use the full time slot when searching for conflicts with other meetings
    const { conflicts, validTimes } = findConflicts(
        finalSlots,
        time,
    )

    // Use the original slot time when check with other meetings
    // This means a click on a 15min slot will cause the times to be joined and not deleted
    const { conflicts: sameMeetingConflicts, validTimes: sameValid } = findConflicts(
        sameMeetingSlots,
        newSlot,
    )

    return {
        slot: time,
        conflicts: conflicts,
        sameMeetingConflicts,
        validTimes,
        sameValid,
    }
}

export const checkPatientDoubleBooking = (
    meeting: CalendarSlot | null | undefined,
    patient: Patient | null | undefined
): {
    valid: boolean,
    message: string,
    meetingData?: PatientMeetingData
} => {

    if (!meeting) {
        return {
            valid: false,
            message: "Invalid Data"
        }
    }

    if (!meeting.patientId) {
        return {
            valid: true,
            message: "Success: Training Meeting"
        }
    }

    if (!patient) {
        return {
            valid: false,
            message: "Invalid Data",
        }
    }

    if (patient.patientId != meeting.patientId) {
        console.error("Patient does not match meeting")
        return {
            valid: true,
            message: "Patient does not match",
        }
    }

    for (let meetingData of patient.meetingData) {
        if (!meetingData.startTime) {
            continue
        }

        if (meetingData.meetingId == meeting.meetingId) {
            continue
        }

        const startTime = new Date(meetingData.startTime)

        const endTime = new Date(meetingData.startTime)
        endTime.setMinutes(endTime.getMinutes() + 30)

        const slot = {
            ...meetingData,
            startTime,
            endTime,
            meetingType: meetingData.meetingType,
        }

        const { status } = getMeetingStatus(slot)

        if (status == MeetingStatus.Cancelled || status == MeetingStatus.Proposed) {
            continue
        }

        if (rangesOverlap(slot, meeting)) {
            return {
                valid: false,
                message: "Patient is already booked for that time slot",
                meetingData,
            }
        }
    }

    return {
        valid: true,
        message: "Success"
    }
}